import React, { useMemo, useState } from "react";
import { Checkbox, Form, Input, Radio, Segmented, Tag } from "antd";
import { LinkOutlined, LockOutlined, PlusOutlined } from "@ant-design/icons";

const PROVIDER_LABEL = { sfs: "SFS", bankable: "Bankable" };

export function getAttachableCompanies(processes, userEmail) {
  const hasChildren = new Set(
    processes.map((p) => p.parent_company_id).filter(Boolean)
  );

  return processes
    .filter(
      (p) =>
        p.created_by === userEmail && p.status === "success" && p.company_id
    )
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .map((p) => ({
      ...p,
      lockedReason: p.parent_company_id
        ? "Already in an organisation"
        : hasChildren.has(p.company_id)
        ? "Already has branches"
        : null,
    }));
}

export function BranchFields({
  form,
  companies,
  isMainEmpty,
  inheritedTags,
  onNameEdited,
  isWalletAvailable,
}) {
  const [query, setQuery] = useState("");
  const mode = Form.useWatch("config.branch.mode", form);
  const attachId = Form.useWatch("config.branch.attachCompanyId", form);

  const visible = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) {
      return companies;
    }
    return companies.filter(
      (c) =>
        (c.company_name || "").toLowerCase().includes(term) ||
        (c.company_id || "").toLowerCase().includes(term)
    );
  }, [companies, query]);

  return (
    <div className="md-branch">
      <Form.Item name="config.branch.mode" noStyle>
        <Radio.Group className="md-choices" aria-label="How to get the branch">
          <Radio.Button value="attach">
            <LinkOutlined />
            <b>Attach one of mine</b>
          </Radio.Button>
          <Radio.Button value="create">
            <PlusOutlined />
            <b>Create a new one</b>
          </Radio.Button>
        </Radio.Group>
      </Form.Item>

      <Form.Item name="config.branch.attachCompanyId" hidden>
        <Input />
      </Form.Item>

      <div className={`md-panel${mode === "attach" ? " is-on" : ""}`}>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search your companies"
          aria-label="Search your companies"
        />
        <div className="md-list" role="radiogroup" aria-label="Your companies">
          {visible.length === 0 && (
            <div className="md-list-empty">
              {companies.length === 0
                ? "You have no ready company yet."
                : "No company matches."}
            </div>
          )}
          {visible.map((c) => {
            const checked = attachId === c.company_id;
            return (
              <button
                key={c.id}
                type="button"
                role="radio"
                aria-checked={checked}
                disabled={Boolean(c.lockedReason)}
                className="md-co"
                onClick={() =>
                  form.setFieldsValue({
                    "config.branch.attachCompanyId": c.company_id,
                  })
                }
              >
                <span className="md-rad" />
                <span className="md-co-main">
                  <span className="md-co-name">{c.company_name}</span>
                  <span className="md-co-id">{c.company_id}</span>
                  {c.lockedReason && (
                    <span className="md-co-why">{c.lockedReason}</span>
                  )}
                </span>
                <span className="md-co-tags">
                  {c.currency && <Tag>{c.currency}</Tag>}
                  {c.banking_provider && (
                    <Tag>
                      {PROVIDER_LABEL[c.banking_provider] || c.banking_provider}
                    </Tag>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className={`md-panel${mode === "create" ? " is-on" : ""}`}>
        <div className="md-grid">
          <Form.Item
            className={isMainEmpty ? "md-s6" : "md-s4"}
            label="Branch name"
            name="config.branch.name"
          >
            <Input autoComplete="off" onChange={onNameEdited} />
          </Form.Item>
          {!isMainEmpty && (
            <Form.Item
              className="md-s2"
              label="Content"
              name="config.branch.content"
            >
              <Segmented
                block
                options={[
                  { label: "Empty", value: "empty" },
                  { label: "Same data", value: "copy" },
                ]}
              />
            </Form.Item>
          )}
        </div>
        <div className="md-inherit">
          <LockOutlined />
          <span>Same as the main company:</span>
          {inheritedTags.map((t) => (
            <Tag key={t}>{t}</Tag>
          ))}
        </div>
      </div>

      {isWalletAvailable && (
        <Form.Item
          name="config.branch.currencyWallet"
          valuePropName="checked"
          noStyle
        >
          <Checkbox className="md-wallet">
            <span className="md-wallet-title">Add a currency branch</span>
            <span className="md-wallet-sub">
              A second EUR wallet in the same organisation, loaded with €30,000
            </span>
          </Checkbox>
        </Form.Item>
      )}
    </div>
  );
}
